import { gql, useQuery } from '@apollo/client';
import Modal from '@orn/shared-web/components/Modal/Modal';
import Select from '@orn/shared-web/components/Select';
import { cx } from '@orn/shared-web/utils/cx';
import * as React from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import {
  AdminWorkItemAssigneeFilter,
  AdminWorkItemSortField,
  WorkItemUserCategory,
} from '../../__generated__/globalTypes';
import AdminSelectUser from '../../components/admin/AdminSelectUser/AdminSelectUser';
import { useSeedSelectionFromLink } from '../../components/admin/AdminSelectUser/useSeedSelectionFromLink';
import Button from '../../components/Button';
import CopyButton from '../../components/CopyButton';
import InspectLink from '../../components/InspectLink';
import InspectLinkedText from '../../components/InspectLinkedText';
import Table from '../../components/Table';
import { assistantThreadDeepLinkPath, readLinkedUserId } from '../../paths';

import CaregiverOnlyBadge, { BADGE_CLASS } from './CaregiverOnlyBadge';
import WorkItemJournalModal from './WorkItemJournalModal';
import {
  abbreviateId,
  DEFAULT_WORK_ITEM_SORT,
  formatWorkItemTimestampFull,
  CELL_PREVIEW_MAX_CHARS,
  toggleWorkItemSort,
  truncateForCell,
  WORK_ITEM_CATEGORY_LABEL,
  WORK_ITEM_ASSIGNEE_LABEL,
  workItemFilterSignature,
  workItemSubjectCounts,
  workItemSubjectRefs,
  type IWorkItemSort,
  type IWorkItemSubjectSource,
} from './workItemsView';
import {
  CELL_TRIGGER_CLASS,
  DateCell,
  EmailCell,
  ExpandableHeaderCell,
  IdCell,
  SortableHeaderCell,
} from './WorkItemTableCells';

import type { AdminListWorkItemsQuery, AdminListWorkItemsQueryVariables } from './__generated__/WorkItemsListScene.gql';
import type { IAdminSelectUserSelection } from '../../components/admin/AdminSelectUser/AdminSelectUser';

const ADMIN_LIST_WORK_ITEMS = gql`
  query AdminListWorkItems(
    $beforeId: ID
    $familyId: ID
    $dueNow: Boolean
    $assignee: AdminWorkItemAssigneeFilter
    $archived: Boolean
    $workItemScheduleId: ID
    $sortField: AdminWorkItemSortField
    $sortDirection: AdminSortDirection
  ) {
    adminListWorkItems(
      beforeId: $beforeId
      familyId: $familyId
      dueNow: $dueNow
      assignee: $assignee
      archived: $archived
      workItemScheduleId: $workItemScheduleId
      sortField: $sortField
      sortDirection: $sortDirection
    ) {
      hasMore
      nextBeforeId
      workItems {
        id
        familyId
        familyMemberCount
        sourceThreadId
        createdByEmail
        assigneeEmail
        dueDate
        archivedAt
        title
        body
        summary
        category
        caregiverOnly
        journal
        transactionIds
        billIds
        merchantIds
        financialAccountId
        bankConnectionId
        recurringTransactionId
        billSubmissionId
        createdAt
        updatedAt
      }
    }
  }
`;

type WorkItemRow = AdminListWorkItemsQuery['adminListWorkItems']['workItems'][number];

// Categorical, not ordinal — matching the hues the same three categories wear
// elsewhere in MOPS so a badge reads the same across tools.
const CATEGORY_BADGE_COLOR: Record<WorkItemUserCategory, string> = {
  [WorkItemUserCategory.Update]: 'bg-utility-blue-light-50 text-utility-blue-light-700',
  [WorkItemUserCategory.Reminder]: 'bg-utility-mist-50 text-utility-mist-700',
  [WorkItemUserCategory.Config]: 'bg-utility-stone-50 text-utility-stone-700',
};

/** Select key for "no assignee filter" — the dropdown needs an option to return to. */
const ASSIGNEE_EVERYONE = 'EVERYONE';

/**
 * What an item's badge explains: the hold is on the synopsis, and only a
 * caregiver's word ends it. Entries are not held wholesale — each carries its
 * own flag, which the journal modal's badge reports.
 */
const ITEM_HELD_TITLE =
  "Synopsis held with the caregivers — off a loved one's read until a caregiver releases the item. Journal entries carry their own flag.";

/**
 * Column widths, in px, summing to the 1232px `main` gets at the 1280px floor.
 *
 * Load-bearing, not decorative: the table is `table-fixed`, which is what makes
 * these authoritative and lets `truncate` work on the cell itself — under
 * `auto` layout the prose columns would push the last columns past the
 * viewport. Id, the three date columns, and Thread share a width — all five
 * hold short mono values.
 */
const COLUMN_WIDTHS_PX = [98, 98, 98, 98, 109, 109, 178, 178, 168, 98];

interface ISynopsisCellProps {
  row: WorkItemRow;
  onOpen: (row: WorkItemRow) => void;
}

/**
 * The household-facing card copy: category badge + headline, or a dash while
 * the item is unexposed (all synopsis fields are null together). Clicking opens
 * the full synopsis in a modal.
 *
 * Who may read the synopsis rides here rather than in a column of its own —
 * it is one state about this cell's own text, and a badge spends no width the
 * fixed layout has to take from somewhere else. It shows on an unexposed row
 * too: a hold can be stamped before there is any card to withhold.
 */
const SynopsisCell = ({ row, onOpen }: ISynopsisCellProps) => {
  const title = row.title ?? null;
  const category = row.category ?? null;
  if (title === null) {
    return (
      <Table.Cell className="text-fg-quaternary text-xs">
        {row.caregiverOnly && <CaregiverOnlyBadge title={ITEM_HELD_TITLE} className="mr-1.5" />}—
      </Table.Cell>
    );
  }
  return (
    <Table.Cell className="text-xs">
      <button type="button" className={CELL_TRIGGER_CLASS} onClick={() => onOpen(row)} title="Open the full synopsis">
        {row.caregiverOnly && <CaregiverOnlyBadge title={ITEM_HELD_TITLE} className="mr-1.5" />}
        {category !== null && (
          <span className={`${BADGE_CLASS} ${CATEGORY_BADGE_COLOR[category]} mr-1.5`}>
            {WORK_ITEM_CATEGORY_LABEL[category]}
          </span>
        )}
        <span className="font-medium">{title}</span>
      </button>
    </Table.Cell>
  );
};

interface IJournalCellProps {
  row: WorkItemRow;
  onOpen: (row: WorkItemRow) => void;
}

/** The record, previewed from the row's window; clicking opens the whole of it. */
const JournalCell = ({ row, onOpen }: IJournalCellProps) => {
  const journal = row.journal ?? null;
  if (journal === null) {
    return <Table.Cell className="text-fg-quaternary">—</Table.Cell>;
  }
  return (
    <Table.Cell className="text-xs">
      <button type="button" className={CELL_TRIGGER_CLASS} onClick={() => onOpen(row)} title="Open the full journal">
        <span className="text-fg-secondary line-clamp-3 break-words">
          {truncateForCell(journal, CELL_PREVIEW_MAX_CHARS)}
        </span>
      </button>
    </Table.Cell>
  );
};

/** The row's link columns as the view helpers read them — undefined folded to null. */
function rowSubjectSource(row: WorkItemRow): IWorkItemSubjectSource {
  return {
    transactionIds: row.transactionIds,
    billIds: row.billIds,
    merchantIds: row.merchantIds,
    financialAccountId: row.financialAccountId ?? null,
    bankConnectionId: row.bankConnectionId ?? null,
    recurringTransactionId: row.recurringTransactionId ?? null,
    billSubmissionId: row.billSubmissionId ?? null,
  };
}

interface ISubjectCellProps {
  row: WorkItemRow;
  onOpen: (row: WorkItemRow) => void;
}

/** Per-kind tallies of the row's foreign keys; clicking opens the full ids. */
const SubjectCell = ({ row, onOpen }: ISubjectCellProps) => {
  const counts = workItemSubjectCounts(rowSubjectSource(row));
  if (counts.length === 0) {
    return <Table.Cell className="text-fg-quaternary">—</Table.Cell>;
  }
  return (
    <Table.Cell className="text-xs">
      <button type="button" className={CELL_TRIGGER_CLASS} onClick={() => onOpen(row)} title="Open the full ids">
        {counts.map((entry) => (
          <span key={entry.label} className="block whitespace-nowrap">
            <span className="text-fg-tertiary text-[10px] uppercase tracking-wider">{entry.label}</span>:{' '}
            <span className="font-mono tabular-nums">{entry.count}</span>
          </span>
        ))}
      </button>
    </Table.Cell>
  );
};

interface IThreadCellProps {
  row: WorkItemRow;
}

/**
 * The assistant thread that authored the row — a deep link into the Threads
 * inspector, opened in a new tab so the operator's filter and paging state
 * survives the jump. Product-maintained rows carry no stamp and dash. The stamp
 * is a hint, not a join key: a deleted thread leaves it dangling, and the
 * inspector's own empty state handles that landing.
 */
const ThreadCell = ({ row }: IThreadCellProps) => {
  const threadId = row.sourceThreadId ?? null;
  if (threadId === null) {
    return <Table.Cell className="text-fg-quaternary">—</Table.Cell>;
  }
  return (
    <Table.Cell mono className="whitespace-nowrap text-xs">
      <Link
        to={assistantThreadDeepLinkPath({ familyId: row.familyId, threadId })}
        target="_blank"
        rel="noopener noreferrer"
        className="text-fg-brand-primary hover:text-fg-brand-secondary underline-offset-2 hover:underline"
        title={threadId}
      >
        {abbreviateId(threadId)}
      </Link>
    </Table.Cell>
  );
};

interface ISynopsisModalProps {
  row: WorkItemRow | null;
  onClose: () => void;
}

/** The full household-facing synopsis — every card field, unabridged. */
const SynopsisModal = ({ row, onClose }: ISynopsisModalProps) => {
  const category = row?.category ?? null;
  return (
    <Modal isOpen={row !== null} onOpenChange={(open) => !open && onClose()} size="lg">
      <Modal.Header onClose={onClose}>Synopsis</Modal.Header>
      <Modal.Body>
        {row !== null && (
          <div className="flex flex-col gap-4 text-sm">
            <div className="flex flex-wrap items-center gap-2">
              {row.caregiverOnly && <CaregiverOnlyBadge title={ITEM_HELD_TITLE} />}
              {category !== null && (
                <span className={`${BADGE_CLASS} ${CATEGORY_BADGE_COLOR[category]}`}>
                  {WORK_ITEM_CATEGORY_LABEL[category]}
                </span>
              )}
              <span className="text-fg-primary font-semibold">{row.title}</span>
            </div>
            {row.body != null && (
              <div className="text-fg-secondary whitespace-pre-wrap break-words">
                <InspectLinkedText text={row.body} />
              </div>
            )}
            {row.summary != null && (
              <div className="border-border-primary border px-3 py-2">
                <div className="text-fg-tertiary mb-1 text-[10px] font-medium uppercase tracking-wider">
                  Summary (raw inline markup)
                </div>
                <div className="text-fg-secondary whitespace-pre-wrap break-words font-mono text-xs">
                  <InspectLinkedText text={row.summary} />
                </div>
              </div>
            )}
          </div>
        )}
      </Modal.Body>
    </Modal>
  );
};

interface ISubjectModalProps {
  row: WorkItemRow | null;
  onClose: () => void;
}

/** Every foreign key the row names, with full copyable ids. */
const SubjectModal = ({ row, onClose }: ISubjectModalProps) => (
  <Modal isOpen={row !== null} onOpenChange={(open) => !open && onClose()} size="lg">
    <Modal.Header onClose={onClose}>Subject</Modal.Header>
    <Modal.Body>
      {row !== null && (
        <div className="flex max-h-[60vh] flex-col gap-1.5 overflow-y-auto text-xs">
          {workItemSubjectRefs(rowSubjectSource(row)).map((ref) => (
            <div key={`${ref.label}-${ref.id}`} className="flex items-baseline gap-2">
              <span className="text-fg-tertiary w-20 shrink-0 text-[10px] uppercase tracking-wider">{ref.label}</span>
              <InspectLink id={ref.id} />
              <CopyButton value={ref.id} label="Copy id" successMessage="Id copied." />
            </div>
          ))}
        </div>
      )}
    </Modal.Body>
  </Modal>
);

interface IWorkItemsPaneProps {
  rows: WorkItemRow[];
  loading: boolean;
  hasMore: boolean;
  pageIndex: number;
  selection: IAdminSelectUserSelection | null;
  familyMemberCount: number | null;
  assignee: AdminWorkItemAssigneeFilter | null;
  dueNow: boolean;
  hideArchived: boolean;
  /** Set while the table is narrowed to one schedule's emissions. */
  scheduleId: string | null;
  /** The `?userId=` a link arrived with that resolved to no member, if any. */
  unresolvedLinkUserId: string | null;
  sort: IWorkItemSort;
  onSort: (field: AdminWorkItemSortField) => void;
  onClearSchedule: () => void;
  onAssigneeChange: (assignee: AdminWorkItemAssigneeFilter | null) => void;
  onToggleDueNow: () => void;
  onToggleHideArchived: () => void;
  onSelectionChange: (selection: IAdminSelectUserSelection | null) => void;
  onOpenSynopsis: (row: WorkItemRow) => void;
  onOpenJournal: (row: WorkItemRow) => void;
  onOpenSubject: (row: WorkItemRow) => void;
  onPrevPage: () => void;
  onNextPage: () => void;
}

/**
 * Presentational half of the Work items tool — user filter above a dense table.
 * Split out from the container so the query/paging wiring stays separate from
 * the rendering.
 */
const BaseWorkItemsPane = ({
  rows,
  loading,
  hasMore,
  pageIndex,
  selection,
  familyMemberCount,
  assignee,
  dueNow,
  hideArchived,
  scheduleId,
  unresolvedLinkUserId,
  sort,
  onSort,
  onClearSchedule,
  onAssigneeChange,
  onToggleDueNow,
  onToggleHideArchived,
  onSelectionChange,
  onOpenSynopsis,
  onOpenJournal,
  onOpenSubject,
  onPrevPage,
  onNextPage,
}: IWorkItemsPaneProps) => (
  <>
    <div className="border-border-primary flex flex-wrap items-start gap-4 border-b pb-4">
      <div className="flex flex-col gap-1">
        <label className="text-fg-tertiary text-xs font-medium uppercase tracking-wider">User</label>
        <AdminSelectUser
          selectedUserId={selection?.userId ?? null}
          onSelectionChange={onSelectionChange}
          placeholder="All users"
          hideLabel
          className="w-80"
        />
      </div>
      <div className="flex flex-col gap-1">
        <span className="text-fg-tertiary text-xs font-medium uppercase tracking-wider">Assignee</span>
        <Select
          aria-label="Assignee"
          placeholder="Everyone"
          value={assignee ?? ASSIGNEE_EVERYONE}
          onChange={(key) =>
            onAssigneeChange(key && key !== ASSIGNEE_EVERYONE ? (key as AdminWorkItemAssigneeFilter) : null)
          }
          items={[
            { id: ASSIGNEE_EVERYONE, label: 'Everyone' },
            ...Object.values(AdminWorkItemAssigneeFilter).map((value) => ({
              id: value,
              label: WORK_ITEM_ASSIGNEE_LABEL[value],
            })),
          ]}
          className="w-48"
        >
          {(item) => <Select.Item id={item.id}>{item.label}</Select.Item>}
        </Select>
      </div>
      <div className="flex flex-col gap-1">
        <span className="text-fg-tertiary text-xs font-medium uppercase tracking-wider">Due</span>
        <Button
          color={dueNow ? 'primary' : 'secondary'}
          size="sm"
          onClick={onToggleDueNow}
          title="Show only items whose due date falls inside the run-up the assistant's queue reads, or that carry no due date at all. Says nothing about who owes the item or whether it is archived."
        >
          Due now
        </Button>
      </div>
      <div className="flex flex-col gap-1">
        <span className="text-fg-tertiary text-xs font-medium uppercase tracking-wider">Archived</span>
        <Button
          color={hideArchived ? 'primary' : 'secondary'}
          size="sm"
          onClick={onToggleHideArchived}
          title="On by default, so finished work stays out of the table; turn it off to list archived items alongside live ones."
        >
          Hide archived
        </Button>
      </div>
    </div>

    {scheduleId !== null && (
      <div className="flex items-center gap-2 text-xs">
        <span className="text-fg-tertiary">
          Showing only the work emitted by schedule <InspectLink id={scheduleId} short />.
        </span>
        <Button color="secondary" size="xs" onClick={onClearSchedule}>
          Clear
        </Button>
      </div>
    )}

    {selection !== null && (
      <span className="text-fg-tertiary text-xs">
        Work items are family-scoped — showing the whole family
        {selection.email ? ` of ${selection.email}` : ''} (
        <InspectLink id={selection.familyId} short />
        {familyMemberCount !== null ? `, ${familyMemberCount} member${familyMemberCount === 1 ? '' : 's'}` : ''}).
      </span>
    )}

    {loading && rows.length === 0 && <span className="text-fg-secondary text-xs">Loading…</span>}

    {!loading && unresolvedLinkUserId !== null && (
      <div className="text-fg-tertiary flex h-32 items-center justify-center text-center text-xs italic">
        This link names member <span className="font-mono not-italic">{unresolvedLinkUserId}</span>, who is not in the
        user list. Pick a member above to search their household.
      </div>
    )}

    {!loading && unresolvedLinkUserId === null && rows.length === 0 && (
      <div className="text-fg-tertiary flex h-32 items-center justify-center text-xs italic">
        No work items here. Either the assistant is idle or the filter is too narrow.
      </div>
    )}

    {rows.length > 0 && (
      <>
        <Table className="table-fixed">
          <colgroup>
            {COLUMN_WIDTHS_PX.map((width, index) => (
              <col key={index} style={{ width }} />
            ))}
          </colgroup>
          <Table.Header sticky>
            <Table.Row>
              <Table.HeaderCell>Id</Table.HeaderCell>
              <SortableHeaderCell field={AdminWorkItemSortField.CreatedAt} sort={sort} onSort={onSort}>
                Created
              </SortableHeaderCell>
              <SortableHeaderCell field={AdminWorkItemSortField.UpdatedAt} sort={sort} onSort={onSort}>
                Updated
              </SortableHeaderCell>
              <SortableHeaderCell field={AdminWorkItemSortField.DueDate} sort={sort} onSort={onSort}>
                Due date
              </SortableHeaderCell>
              <Table.HeaderCell>Created by</Table.HeaderCell>
              <Table.HeaderCell>Assignee</Table.HeaderCell>
              <ExpandableHeaderCell>Synopsis</ExpandableHeaderCell>
              <ExpandableHeaderCell>Journal</ExpandableHeaderCell>
              <ExpandableHeaderCell>Subject</ExpandableHeaderCell>
              <Table.HeaderCell>Thread</Table.HeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {rows.map((row) => {
              const archivedAt = row.archivedAt ?? null;
              return (
                // Archival has no column of its own — it is one state, and dimming
                // the row spends no width to say it.
                //
                // `align-top` because cells default to align-middle, which
                // detaches the short cells from their row once the prose wraps.
                <Table.Row
                  key={row.id}
                  className={cx('[&>td]:align-top', archivedAt !== null && 'opacity-45')}
                  title={archivedAt !== null ? `Archived ${formatWorkItemTimestampFull(archivedAt)}` : undefined}
                >
                  <IdCell id={row.id} />
                  <DateCell iso={row.createdAt} />
                  <DateCell iso={row.updatedAt} />
                  <DateCell iso={row.dueDate ?? null} />
                  <EmailCell email={row.createdByEmail ?? null} />
                  {/* Null means the assistant holds the work — the default, worth
                      naming so it never reads as missing data. */}
                  <EmailCell email={row.assigneeEmail ?? null} nullLabel="Assistant" />
                  <SynopsisCell row={row} onOpen={onOpenSynopsis} />
                  <JournalCell row={row} onOpen={onOpenJournal} />
                  <SubjectCell row={row} onOpen={onOpenSubject} />
                  <ThreadCell row={row} />
                </Table.Row>
              );
            })}
          </Table.Body>
        </Table>

        <div className="flex items-center gap-3">
          <Button color="secondary" size="xs" onClick={onPrevPage} isDisabled={pageIndex === 0 || loading}>
            Prev
          </Button>
          <Button color="secondary" size="xs" onClick={onNextPage} isDisabled={!hasMore || loading}>
            Next
          </Button>
          <span className="text-fg-tertiary text-xs">
            Page {pageIndex + 1} · {rows.length} row{rows.length === 1 ? '' : 's'}
          </span>
          {!hideArchived && <span className="text-fg-tertiary text-xs">Dimmed rows are archived</span>}
        </div>
      </>
    )}
  </>
);

/**
 * Items tab of the Work items tool. Lists work items across every family — the
 * assistant's private, unexposed rows included — most recently updated first,
 * or on whichever date column the operator clicks, each linking to the
 * assistant thread that authored it. The Synopsis and Journal cells open their
 * full text in a modal. Read-only: creating, exposing, assigning, and archiving
 * happen in the consumer product.
 *
 * The User filter sends `familyId`, not `userId`: work items are family-scoped.
 * Archived rows are left out until Hide archived is off, and Due now reads the
 * same run-up ahead of a due date the assistant's own queue does.
 *
 * A `scheduleId` in the query string narrows the table to what one schedule has
 * emitted — the link the Schedules tab hands over. A `userId` pre-selects that
 * user's household in the filter, which is how the Accounts tool hands a
 * household over.
 */
const WorkItemsListScene = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const scheduleId = searchParams.get('scheduleId');
  const [selection, setSelection] = React.useState<IAdminSelectUserSelection | null>(null);
  // A `?userId=` link lands here with an empty picker, so seed it from that
  // user's own record once the list the picker shares has loaded.
  const isLinkSeedPending = useSeedSelectionFromLink(setSelection);
  const [linkUserId] = React.useState(() => readLinkedUserId(searchParams));
  // Set once the operator touches the picker, which is them speaking over the
  // link — including when they clear it to ask for every household.
  const [hasOverriddenLink, setHasOverriddenLink] = React.useState(false);
  // Computed during render rather than in an effect: the seed drops its
  // pending flag and this query is enabled in the same render, so a guard that
  // waited for an effect would let one fleet-wide page fire under a
  // household's link before it took hold.
  const isLinkUnresolved = linkUserId !== null && !isLinkSeedPending && selection === null && !hasOverriddenLink;

  const [assignee, setAssignee] = React.useState<AdminWorkItemAssigneeFilter | null>(null);
  const [dueNow, setDueNow] = React.useState(false);
  const [hideArchived, setHideArchived] = React.useState(scheduleId === null);
  const [sort, setSort] = React.useState<IWorkItemSort>(DEFAULT_WORK_ITEM_SORT);
  // Forward-only keyset, so Prev pops a stack rather than querying backwards.
  const [cursorStack, setCursorStack] = React.useState<string[]>([]);
  const [synopsisRow, setSynopsisRow] = React.useState<WorkItemRow | null>(null);
  const [journalRow, setJournalRow] = React.useState<WorkItemRow | null>(null);
  const [subjectRow, setSubjectRow] = React.useState<WorkItemRow | null>(null);

  const { data, previousData, loading } = useQuery<AdminListWorkItemsQuery, AdminListWorkItemsQueryVariables>(
    ADMIN_LIST_WORK_ITEMS,
    {
      variables: {
        beforeId: cursorStack.at(-1) ?? null,
        familyId: selection?.familyId ?? null,
        dueNow,
        assignee,
        // Null is "both"; the tool's own default is to leave finished work out.
        archived: hideArchived ? false : null,
        workItemScheduleId: scheduleId,
        sortField: sort.field,
        sortDirection: sort.direction,
      },
      // Held while a link's user is still resolving: the unfiltered fleet-wide
      // page would otherwise load first and be replaced, which reads as the
      // link having been ignored. Held for good once that seed comes back
      // empty, because a household-scoped link answered with every family's
      // work reads as the household's own.
      skip: isLinkSeedPending || isLinkUnresolved,
      fetchPolicy: 'cache-and-network',
      notifyOnNetworkStatusChange: true,
    }
  );

  // Hold the last page on screen while the next one loads, but *only* across a
  // cursor move. Apollo empties `data` for any variable set it hasn't cached, so
  // without this the table and its own pager vanish mid-page-turn. Reusing it
  // across a filter change instead would render stale rows as though they
  // matched the new filter.
  const filterSignature = workItemFilterSignature({
    familyId: selection?.familyId ?? null,
    assignee,
    dueNow,
    hideArchived,
    scheduleId,
    sort,
  });
  const renderedSignature = React.useRef(filterSignature);
  const onlyCursorMoved = renderedSignature.current === filterSignature;
  React.useEffect(() => {
    renderedSignature.current = filterSignature;
  }, [filterSignature]);

  const reusablePage = onlyCursorMoved ? previousData : undefined;
  const listing = (data ?? reusablePage)?.adminListWorkItems;
  const rows = listing?.workItems ?? [];

  // A stale cursor starts mid-window under the new filter, so every filter
  // change restarts paging.
  const handleSelectionChange = (next: IAdminSelectUserSelection | null) => {
    setSelection(next);
    setCursorStack([]);
    setHasOverriddenLink(true);
  };

  const handleAssigneeChange = (next: AdminWorkItemAssigneeFilter | null) => {
    setAssignee(next);
    setCursorStack([]);
  };

  const handleNextPage = () => {
    const nextBeforeId = listing?.nextBeforeId;
    if (listing?.hasMore && nextBeforeId) {
      setCursorStack((prev) => [...prev, nextBeforeId]);
    }
  };

  const handlePrevPage = () => setCursorStack((prev) => prev.slice(0, -1));

  const handleToggleDueNow = () => {
    setDueNow((prev) => !prev);
    setCursorStack([]);
  };

  const handleToggleHideArchived = () => {
    setHideArchived((prev) => !prev);
    setCursorStack([]);
  };

  const handleSort = (field: AdminWorkItemSortField) => {
    setSort((prev) => toggleWorkItemSort(prev, field));
    setCursorStack([]);
  };

  // Arriving on a schedule's emissions with the archived filter on would hide
  // most of them: what the schedule emitted has since been worked and put away.
  React.useEffect(() => {
    if (scheduleId !== null) {
      setHideArchived(false);
    }
  }, [scheduleId]);

  const handleClearSchedule = () => {
    const next = new URLSearchParams(searchParams);
    next.delete('scheduleId');
    setSearchParams(next, { replace: true });
    setCursorStack([]);
  };

  return (
    <>
      <BaseWorkItemsPane
        rows={rows}
        // A skipped query reports `loading: false`, so without the seed flag the
        // empty state claims there is nothing here before the query ever runs.
        loading={loading || isLinkSeedPending}
        hasMore={listing?.hasMore ?? false}
        pageIndex={cursorStack.length}
        selection={selection}
        familyMemberCount={rows[0]?.familyMemberCount ?? null}
        assignee={assignee}
        dueNow={dueNow}
        hideArchived={hideArchived}
        scheduleId={scheduleId}
        unresolvedLinkUserId={isLinkUnresolved ? linkUserId : null}
        sort={sort}
        onSort={handleSort}
        onClearSchedule={handleClearSchedule}
        onAssigneeChange={handleAssigneeChange}
        onToggleDueNow={handleToggleDueNow}
        onToggleHideArchived={handleToggleHideArchived}
        onSelectionChange={handleSelectionChange}
        onOpenSynopsis={setSynopsisRow}
        onOpenJournal={setJournalRow}
        onOpenSubject={setSubjectRow}
        onPrevPage={handlePrevPage}
        onNextPage={handleNextPage}
      />
      <SynopsisModal row={synopsisRow} onClose={() => setSynopsisRow(null)} />
      <WorkItemJournalModal workItem={journalRow} onClose={() => setJournalRow(null)} />
      <SubjectModal row={subjectRow} onClose={() => setSubjectRow(null)} />
    </>
  );
};

export default WorkItemsListScene;
